"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const fmko_ts_common_1 = require("fmko-ts-common");
const LabeledInputFieldWidget_1 = tslib_1.__importDefault(require("../widget/LabeledInputFieldWidget"));
const InputEventUtil_1 = tslib_1.__importDefault(require("../util/InputEventUtil"));
class WCAGCPRField extends LabeledInputFieldWidget_1.default {
    constructor(args) {
        super({
            ...args,
            fieldSizeClass: args.fieldSizeClass,
            hint: args.hint !== undefined ? args.hint : "(ddmmåå-xxxx)",
            inputType: "text",
            updateOnInputChange: false
        });
        const { allowECPR = true } = args;
        this.allowECPR = allowECPR;
    }
    isValid() {
        const value = super.getValue();
        return this.isValidInputString(value);
    }
    isValidInputString(value) {
        if (!value) {
            return false;
        }
        return this.getRegExp().test(value);
    }
    getValue() {
        const v = super.getValue();
        if (v) {
            return this.parseFieldElementValue(v);
        }
        return undefined;
    }
    setValue(value, fireEvents = false) {
        if (value === "") {
            value = undefined;
        }
        super.setValue(value, false);
        // only fire valueChanged when CPR changes from a full-length CPR-value to something else, or vice versa
        const v = (value?.length === WCAGCPRField.CPR_MAX_LENGTH && this.isValid()) ? value : undefined;
        if (this.previousValue !== v) {
            if (fireEvents) {
                fmko_ts_common_1.ValueChangeEvent.fire(this, v);
            }
            this.previousValue = v;
        }
    }
    parseFieldElementValue(inputValue) {
        if (!this.getRegExp().test(inputValue?.toUpperCase())) {
            return undefined;
        }
        return inputValue.toUpperCase();
    }
    setFieldElementValue(value) {
        // Update field value
        if (!value) {
            this.getFieldElement().value = "";
        }
        else {
            this.getFieldElement().value = value;
        }
    }
    decorateLabeledInputFieldWidgetElement(args) {
        this.element = document.createElement("div");
        this.element.classList.add("cpr-field");
        const field = this.getFieldElement();
        field.maxLength = WCAGCPRField.CPR_MAX_LENGTH;
        this.add(this.labelElement);
        this.add(this.fieldElement);
        this.add(this.hintElement);
    }
    onInput(event) {
        let pos = undefined;
        if (!InputEventUtil_1.default.isInsertingText(event)) {
            pos = this.fieldElement.selectionStart;
        }
        this.insertDashAndTrimCprNumber(this.fieldElement.value, event.data);
        if (pos !== undefined && pos !== this.fieldElement.selectionStart) {
            this.fieldElement.selectionStart = pos;
            this.fieldElement.selectionEnd = pos;
        }
    }
    onPaste(event) {
        // We have to prevent the paste behaviour as we are setting the field ourselves
        event.preventDefault();
        const afterPasting = this.buildPasteString(event);
        const hasDash = afterPasting.includes("-");
        const isValidCpr = this.getRegExp().test(afterPasting.toUpperCase());
        let isValid;
        if ((afterPasting.length >= WCAGCPRField.CPR_MAX_LENGTH) && hasDash && !isValidCpr) {
            isValid = false;
        }
        else if ((afterPasting.length >= WCAGCPRField.CPR_MAX_LENGTH - 1) && !hasDash && !isValidCpr) {
            isValid = false;
        }
        else {
            isValid = super.isValidInputString(afterPasting);
        }
        if (isValid) {
            this.insertDashAndTrimCprNumber(afterPasting);
        }
    }
    isValidNextChar(oldValue, char, selectionStart = null, selectionEnd = null) {
        if (char === "Backspace" || char === "Delete") {
            return true;
        }
        if (selectionStart === 6 && char === "-") {
            return true;
        }
        const hasDash = oldValue.includes("-");
        const trimmedValue = oldValue
            .replace("-", "")
            .toUpperCase()
            .trim();
        const ecprIndexSelected = hasDash
            ? selectionStart === 8 || selectionStart === 9
            : selectionStart === 7 || selectionStart === 8;
        const isNumber = /^\d$/.test(char);
        if (!ecprIndexSelected || !this.allowECPR) {
            return isNumber;
        }
        else {
            if (ecprIndexSelected) {
                const isValidECPRLetter = /^[A-Za-zÆØÅæøå]$/.test(char);
                if (trimmedValue.length === 8) {
                    // number or - on the left side of the letter, only 1 letter and then number again
                    const hasOneECPR = /^[\d|-]+[A-Za-zÆØÅæøå]\d*$/.test(trimmedValue);
                    return (hasOneECPR && isValidECPRLetter) || (!hasOneECPR && isNumber);
                }
                return isValidECPRLetter || isNumber;
            }
            return isNumber;
        }
    }
    getRegExp() {
        return this.allowECPR
            ? WCAGCPRField.CPR_REGEXP_ECPR
            : WCAGCPRField.CPR_REGEXP;
    }
    insertDashAndTrimCprNumber(newValue, nextChar) {
        let selectionStart;
        const fieldElement = this.fieldElement;
        if (nextChar) {
            selectionStart = fieldElement.selectionStart === WCAGCPRField.CURSOR_BEFORE_DASH + 1
                ? fieldElement.selectionStart + 1 + (fieldElement.value.includes("-") ? 0 : 1)
                : fieldElement.selectionStart;
        }
        const trimmedValue = newValue
            .replace("-", "")
            .toUpperCase()
            .trim();
        let nextValue;
        if (newValue.length > WCAGCPRField.CURSOR_BEFORE_DASH - (newValue.includes("-") ? -1 : 0)) {
            nextValue = `${trimmedValue.substring(0, WCAGCPRField.CURSOR_BEFORE_DASH)}-${trimmedValue.substring(WCAGCPRField.CURSOR_BEFORE_DASH)}`.trim();
        }
        else {
            nextValue = trimmedValue;
        }
        this.setValue(nextValue, true);
        if (selectionStart) {
            fieldElement.selectionStart = selectionStart;
            fieldElement.selectionEnd = selectionStart;
        }
    }
}
WCAGCPRField.CPR_REGEXP = /^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[012])\d\d-?\d\d\d\d$/;
WCAGCPRField.CPR_REGEXP_ECPR = /^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[012])\d\d-?\d[A-Z0-9ÆØÅ][A-Z0-9ÆØÅ]\d$/;
WCAGCPRField.CPR_MAX_LENGTH = 11;
WCAGCPRField.CURSOR_BEFORE_DASH = 6;
exports.default = WCAGCPRField;
//# sourceMappingURL=WCAGCPRField.js.map